import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  // Handle scroll for navbar
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', () => {
      setScrolled(window.scrollY > 50);
    });
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4tvvo19ncmb029djno0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <h1 className={`text-2xl font-bold tracking-wider transition-colors ${scrolled ? 'text-gray-900' : 'text-white'}`}>
                EL NENE S.R.L.
              </h1>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#home" className={`transition-colors hover:text-cyan-400 whitespace-nowrap ${scrolled ? 'text-gray-700' : 'text-white'}`}>Home</a>
              <a href="#about" className={`transition-colors hover:text-cyan-400 whitespace-nowrap ${scrolled ? 'text-gray-700' : 'text-white'}`}>About</a>
              <a href="#vpn-service" className={`transition-colors hover:text-cyan-400 whitespace-nowrap ${scrolled ? 'text-gray-700' : 'text-white'}`}>VPN Service</a>
              <a href="#features" className={`transition-colors hover:text-cyan-400 whitespace-nowrap ${scrolled ? 'text-gray-700' : 'text-white'}`}>Features</a>
              <a href="#contact" className={`transition-colors hover:text-cyan-400 whitespace-nowrap ${scrolled ? 'text-gray-700' : 'text-white'}`}>Contact</a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            >
              <i className={`ri-menu-line text-2xl ${scrolled ? 'text-gray-900' : 'text-white'}`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-3">
              <a href="#home" className="block text-gray-700 hover:text-cyan-400 transition-colors whitespace-nowrap" onClick={() => setIsMenuOpen(false)}>Home</a>
              <a href="#about" className="block text-gray-700 hover:text-cyan-400 transition-colors whitespace-nowrap" onClick={() => setIsMenuOpen(false)}>About</a>
              <a href="#vpn-service" className="block text-gray-700 hover:text-cyan-400 transition-colors whitespace-nowrap" onClick={() => setIsMenuOpen(false)}>VPN Service</a>
              <a href="#features" className="block text-gray-700 hover:text-cyan-400 transition-colors whitespace-nowrap" onClick={() => setIsMenuOpen(false)}>Features</a>
              <a href="#contact" className="block text-gray-700 hover:text-cyan-400 transition-colors whitespace-nowrap" onClick={() => setIsMenuOpen(false)}>Contact</a>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 w-full h-full">
          <img 
            src="https://public.readdy.ai/ai/img_res/4c691009f83455d8714d55b29d2232d4.jpg"
            alt="VPN Security Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="max-w-2xl">
            <h2 className="text-7xl font-bold text-white mb-6 leading-tight">
              Secure Your<br />
              <span className="font-light">Digital Freedom</span>
            </h2>
            <p className="text-xl text-gray-300 mb-12 leading-relaxed">
              Enterprise-grade VPN protection for your privacy
            </p>
            <a 
              href="#contact"
              className="inline-block px-12 py-4 bg-gradient-to-r from-cyan-400 to-cyan-600 text-white font-semibold rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Get Started
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-4">WHO WE ARE</p>
          </div>
          
          <div className="flex flex-col lg:flex-row gap-16">
            <div className="lg:w-3/5 lg:ml-auto">
              <h3 className="text-5xl font-bold text-gray-900 mb-20 leading-tight">
                Protecting Privacy Through Innovation
              </h3>
              
              <div className="space-y-8 text-lg text-gray-600 leading-relaxed max-w-xl">
                <p>
                  At EL NENE S.R.L., we are dedicated to providing cutting-edge VPN solutions that safeguard your digital privacy. Our mission is to ensure that everyone can browse the internet freely and securely, without compromising their personal information.
                </p>
                <p>
                  We leverage the latest encryption technologies and security protocols to create a robust shield around your online activities. Our commitment to innovation drives us to continuously improve our services, staying ahead of emerging threats in the digital landscape.
                </p>
                <p>
                  Based in Trelew, Argentina, we serve users worldwide with reliable, fast, and secure VPN connections. Our team of security experts works tirelessly to maintain the highest standards of privacy protection for all our clients.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-32 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="rounded-3xl overflow-hidden shadow-lg hover:scale-[1.02] transition-transform duration-300">
              <img 
                src="https://readdy.ai/api/search-image?query=Abstract%20visualization%20of%20cybersecurity%20shield%20protecting%20digital%20data%20with%20glowing%20blue%20and%20cyan%20encryption%20symbols%20floating%20in%20a%20dark%20technological%20space%20representing%20VPN%20security%20and%20privacy%20protection%20with%20modern%20minimalist%20aesthetic&width=800&height=600&seq=about-img-001&orientation=landscape"
                alt="Security Visualization"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="rounded-3xl overflow-hidden shadow-lg hover:scale-[1.02] transition-transform duration-300">
              <img 
                src="https://readdy.ai/api/search-image?query=Futuristic%20network%20infrastructure%20with%20interconnected%20server%20nodes%20and%20data%20pathways%20illuminated%20in%20cyan%20and%20purple%20lights%20showing%20global%20VPN%20connectivity%20across%20continents%20in%20a%20sleek%20dark%20environment%20with%20geometric%20patterns&width=900&height=600&seq=about-img-002&orientation=landscape"
                alt="Network Infrastructure"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* VPN Service Section */}
      <section id="vpn-service" className="py-40 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto mb-24">
            <p className="text-sm uppercase tracking-wide text-cyan-400 mb-6">OUR SOLUTION</p>
            <h3 className="text-6xl font-bold text-white mb-8">
              Military-Grade VPN Protection
            </h3>
            <p className="text-xl text-gray-400 leading-relaxed">
              Experience unparalleled security with our advanced VPN technology designed to keep your data safe from prying eyes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Card 1 */}
            <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8 hover:border-cyan-400 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <h4 className="text-2xl font-bold text-white mb-4">
                Advanced Encryption
              </h4>
              <p className="text-gray-400 leading-relaxed">
                Protect your data with AES-256 encryption, the same standard used by governments and security experts worldwide to secure classified information.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8 hover:border-cyan-400 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-lock-line text-3xl text-white"></i>
              </div>
              <h4 className="text-2xl font-bold text-white mb-4">
                Zero-Log Policy
              </h4>
              <p className="text-gray-400 leading-relaxed">
                We never track, collect, or share your private data. Your online activities remain completely anonymous and secure at all times.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8 hover:border-cyan-400 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-global-line text-3xl text-white"></i>
              </div>
              <h4 className="text-2xl font-bold text-white mb-4">
                Global Network
              </h4>
              <p className="text-gray-400 leading-relaxed">
                Access content from anywhere with our worldwide server network, ensuring fast and reliable connections no matter where you are.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-20">
            <div>
              <h3 className="text-5xl font-bold text-gray-900 leading-tight">
                Why Choose<br />Our VPN
              </h3>
            </div>
            <div className="flex items-start">
              <p className="text-lg text-gray-600 leading-relaxed max-w-md">
                Our VPN service combines cutting-edge technology with user-friendly design to deliver the ultimate privacy protection experience.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-10 gap-10">
            {/* Feature 1 - Larger */}
            <div className="md:col-span-4 bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="w-full h-64">
                <img 
                  src="https://readdy.ai/api/search-image?query=Lightning%20fast%20speed%20concept%20with%20glowing%20cyan%20light%20trails%20and%20velocity%20motion%20blur%20in%20dark%20space%20representing%20high-speed%20VPN%20connection%20and%20data%20transfer%20with%20modern%20minimalist%20design%20and%20dynamic%20energy&width=600&height=450&seq=feature-img-001&orientation=landscape"
                  alt="Lightning Fast Speed"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8">
                <h4 className="text-3xl font-bold text-gray-900 mb-4">
                  Lightning Fast Speed
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  Experience blazing-fast connection speeds without compromising security. Our optimized servers ensure smooth streaming, gaming, and browsing.
                </p>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="md:col-span-3 bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="p-8 text-center">
                <h4 className="text-2xl font-bold text-gray-900 mb-6">
                  Easy to Use
                </h4>
              </div>
              <div className="w-full h-56">
                <img 
                  src="https://readdy.ai/api/search-image?query=Simple%20one-click%20interface%20design%20with%20clean%20modern%20button%20and%20intuitive%20user%20experience%20elements%20in%20cyan%20and%20white%20colors%20on%20dark%20background%20representing%20easy%20VPN%20connection%20with%20minimalist%20aesthetic&width=500&height=400&seq=feature-img-002&orientation=portrait"
                  alt="Easy to Use"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Feature 3 */}
            <div className="md:col-span-3 bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="p-8 text-center">
                <h4 className="text-2xl font-bold text-gray-900 mb-6">
                  24/7 Support
                </h4>
              </div>
              <div className="w-full h-56">
                <img 
                  src="https://readdy.ai/api/search-image?query=Customer%20support%20concept%20with%20friendly%20assistance%20symbols%20and%20communication%20icons%20in%20cyan%20glow%20on%20dark%20background%20representing%20round-the-clock%20VPN%20technical%20support%20with%20modern%20professional%20design&width=500&height=400&seq=feature-img-003&orientation=portrait"
                  alt="24/7 Support"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-40 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-11 gap-16">
            {/* Form Area */}
            <div className="lg:col-span-6">
              <h3 className="text-5xl font-bold text-gray-900 mb-6">
                Get In Touch
              </h3>
              <p className="text-lg text-gray-600 mb-12">
                Questions about our VPN service? Contact us.
              </p>

              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name"
                    required
                    className="w-full h-14 px-5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-cyan-400 transition-colors"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Your Email"
                    required
                    className="w-full h-14 px-5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-cyan-400 transition-colors"
                  />
                </div>
                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Your Message (max 500 characters)"
                    required
                    maxLength={500}
                    rows={6}
                    className="w-full px-5 py-4 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-cyan-400 transition-colors resize-none"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-2">{formData.message.length}/500 characters</p>
                </div>
                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full h-15 bg-gradient-to-r from-cyan-400 to-cyan-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50"
                >
                  {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                </button>
                {formStatus === 'success' && (
                  <p className="text-green-600 text-sm">Message sent successfully!</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-red-600 text-sm">Failed to send message. Please try again.</p>
                )}
              </form>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-5">
              <div className="bg-gray-900 rounded-2xl p-12 text-white h-full">
                <h4 className="text-2xl font-bold mb-10">EL NENE S.R.L.</h4>
                
                <div className="space-y-10">
                  <div>
                    <p className="text-sm text-gray-400 mb-2">Email</p>
                    <a href="mailto:contact@elnenesrl.com" className="text-lg hover:text-cyan-400 transition-colors cursor-pointer">
                      contact@elnenesrl.com
                    </a>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-400 mb-2">Phone</p>
                    <a href="tel:+12568418730" className="text-lg hover:text-cyan-400 transition-colors cursor-pointer">
                      +1 2568418730
                    </a>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-400 mb-2">Address</p>
                    <p className="text-lg leading-relaxed">
                      12 DE OCTUBRE 449<br />
                      Trelew, U9102BAI<br />
                      Argentina
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            {/* Brand Column */}
            <div>
              <h5 className="text-xl font-bold mb-4">EL NENE S.R.L.</h5>
              <p className="text-sm text-gray-400 mb-8">Secure VPN Solutions</p>
            </div>

            {/* Product Column */}
            <div>
              <h6 className="text-base font-bold uppercase tracking-wider mb-6">Product</h6>
              <ul className="space-y-4">
                <li><a href="#features" className="text-sm text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer">Features</a></li>
                <li><a href="#vpn-service" className="text-sm text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer">Security</a></li>
                <li><a href="#vpn-service" className="text-sm text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer">Servers</a></li>
              </ul>
            </div>

            {/* Company Column */}
            <div>
              <h6 className="text-base font-bold uppercase tracking-wider mb-6">Company</h6>
              <ul className="space-y-4">
                <li><a href="#about" className="text-sm text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer">About</a></li>
                <li><a href="#contact" className="text-sm text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer">Contact</a></li>
                <li><a href="#" className="text-sm text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer">Privacy</a></li>
              </ul>
            </div>

            {/* Support Column */}
            <div>
              <h6 className="text-base font-bold uppercase tracking-wider mb-6">Support</h6>
              <ul className="space-y-4">
                <li><a href="#contact" className="text-sm text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer">Help Center</a></li>
                <li><a href="#features" className="text-sm text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer">Documentation</a></li>
                <li><a href="#contact" className="text-sm text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer">FAQ</a></li>
              </ul>
            </div>
          </div>

          {/* Lower Footer */}
          <div className="pt-12 border-t border-gray-800">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <p className="text-sm text-gray-400">© 2025 EL NENE S.R.L. All rights reserved.</p>
              
              <div className="flex items-center gap-5">
                <a href="#" className="w-9 h-9 border border-gray-600 rounded-lg flex items-center justify-center hover:bg-cyan-400 hover:border-cyan-400 transition-all cursor-pointer">
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
                <a href="#" className="w-9 h-9 border border-gray-600 rounded-lg flex items-center justify-center hover:bg-cyan-400 hover:border-cyan-400 transition-all cursor-pointer">
                  <i className="ri-twitter-x-fill text-lg"></i>
                </a>
                <a href="#" className="w-9 h-9 border border-gray-600 rounded-lg flex items-center justify-center hover:bg-cyan-400 hover:border-cyan-400 transition-all cursor-pointer">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
                <a href="#" className="w-9 h-9 border border-gray-600 rounded-lg flex items-center justify-center hover:bg-cyan-400 hover:border-cyan-400 transition-all cursor-pointer">
                  <i className="ri-instagram-fill text-lg"></i>
                </a>
              </div>

              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
